import { useState } from 'react';
import { t, hasKey } from '@app/i18n';
import { dataHour } from '@app/format';
import { Alert, Button, ErrorBanner, Field, FieldGrid, LoadingButton, TextInput, copyText, type CopyState } from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { fieldMessage } from '@app/errors';
import { useCreateAdmissionToken, useRegisterAdmission, useRevokeAdmissionToken } from './SiteAdmission.api';
import { admissionCode, installCommand, type AdmissionToken, type SiteAdmission } from './SiteAdmissionModel';
import s from './SiteAdmission.module.css';

export interface AdmissionRegistrationProps {
  siteId: string;
  registration: SiteAdmission['registration'];
  /** `again` = «Registrar de novo» (reinstalação): o endereço vem preenchido e há como desistir. */
  mode: 'first' | 'again';
  onCancel?: () => void;
  onRegistered: () => void;
}

/**
 * Os três passos do protótipo §4. O token aparece só na resposta que o gera; o produto guarda o hash. Por isso ele
 * fica NA MEMÓRIA desta tela entre o passo 1 e o 3, e só quando a página recarregou a pessoa precisa colá-lo de
 * novo (o campo «Token» aparece só nesse caso; decisão registrada em docs/progress/admission-config.md).
 */
export function AdmissionRegistration({ siteId, registration, mode, onCancel, onRegistered }: AdmissionRegistrationProps) {
  const [token, setToken] = useState<AdmissionToken | null>(null);
  const [pasted, setPasted] = useState('');
  const [endpoint, setEndpoint] = useState(registration.endpoint);
  const [copy, setCopy] = useState<CopyState>('idle');
  const create = useCreateAdmissionToken(siteId);
  const revoke = useRevokeAdmissionToken(siteId);
  const register = useRegisterAdmission(siteId);

  const pendingElsewhere = !token && registration.token_pending;
  const tokenValue = token?.token ?? pasted.trim();

  function generate() {
    register.reset();
    create.mutate(undefined, {
      onSuccess: (created) => {
        setToken(created);
        setCopy('idle');
      },
    });
  }

  function submit() {
    register.mutate({ endpoint: endpoint.trim(), token: tokenValue }, { onSuccess: onRegistered });
  }

  const endpointError = register.error?.campos.find((campo) => campo.field === 'endpoint');

  return (
    <div className={s.stack}>
      {registration.token_expired && !token ? (
        <Alert tone="warning" title={t('sites.admission.register.expired.title')}>
          {t('sites.admission.register.expired.message')}
        </Alert>
      ) : null}
      <ol className={s.steps}>
        <li>
          <p>{t('sites.admission.register.step1')}</p>
          <div className={s.row}>
            <LoadingButton
              variant="out"
              icon="key"
              loading={create.isPending}
              loadingLabel={t('sites.admission.register.generating')}
              onClick={generate}
            >
              {t(token || pendingElsewhere ? 'sites.admission.register.generateAgain' : 'sites.admission.register.generate')}
            </LoadingButton>
            {pendingElsewhere ? (
              <Button
                variant="soft"
                onClick={() => revoke.mutate()}
                disabled={revoke.isPending}
              >
                {t('sites.admission.register.revoke')}
              </Button>
            ) : null}
          </div>
          {pendingElsewhere ? (
            <p className={s.note} data-testid="admission-token-pending">
              {t('sites.admission.register.pending', {
                who: registration.token_created_by || '—',
                when: dataHour(registration.token_expires_at) ?? '—',
              })}
            </p>
          ) : null}
          {create.error ? <ErrorBanner error={create.error} title={t('sites.admission.register.error.token')} hideLink /> : null}
          {revoke.error ? <ErrorBanner error={revoke.error} title={t('sites.admission.register.error.revoke')} hideLink /> : null}
          {token ? (
            <div className={s.tokenBox} data-testid="admission-token">
              <div className={s.row}>
                <span>{t('sites.admission.register.tokenOnce')}</span>
                <code className={s.mono}>{token.token}</code>
                <Button
                  variant="soft"
                  onClick={() => void copyText(token.token).then((ok) => setCopy(ok ? 'done' : 'failed'))}
                >
                  {t(copy === 'done' ? 'sites.admission.register.copied' : copy === 'failed' ? 'sites.admission.register.copyFailed' : 'sites.admission.register.copy')}
                </Button>
              </div>
              <p className={s.note}>{t('sites.admission.register.useInInstall')}</p>
              <pre className={s.command}>{installCommand(token.token, token.expires_at)}</pre>
            </div>
          ) : null}
        </li>
        <li>
          <p>{t('sites.admission.register.step2')}</p>
          <FieldGrid>
            <Field
              label={t('sites.admission.register.endpoint')}
              span={12}
              required
              tip={t('sites.admission.register.endpoint.tip')}
              error={endpointError ? fieldMessage(endpointError) : undefined}
            >
              {(props) => (
                <TextInput
                  {...props}
                  mono
                  autoComplete="off"
                  value={endpoint}
                  placeholder="https://segura-agent-admission-config.segura-aim.svc"
                  onChange={(event) => setEndpoint(event.target.value)}
                />
              )}
            </Field>
            {!token ? (
              <Field
                label={t('sites.admission.register.token')}
                span={12}
                required
                help={t('sites.admission.register.token.help')}
              >
                {(props) => (
                  <TextInput
                    {...props}
                    mono
                    type="password"
                    autoComplete="off"
                    value={pasted}
                    onChange={(event) => setPasted(event.target.value)}
                  />
                )}
              </Field>
            ) : null}
          </FieldGrid>
        </li>
        <li>
          <p>{t('sites.admission.register.step3')}</p>
          {register.error ? <RegisterError error={register.error} /> : null}
          <div className={s.row}>
            {onCancel ? (
              <Button variant="out" onClick={onCancel}>
                {t('sites.admission.register.cancel')}
              </Button>
            ) : null}
            <LoadingButton
              loading={register.isPending}
              loadingLabel={t('sites.admission.register.registering')}
              disabled={!endpoint.trim() || !tokenValue}
              onClick={submit}
            >
              {t(mode === 'again' ? 'sites.admission.register.submitAgain' : 'sites.admission.register.submit')}
            </LoadingButton>
          </div>
        </li>
      </ol>
    </div>
  );
}

/**
 * A recusa do próprio admission diz por quê em `data.admission_code`; a frase vem do catálogo quando o código é
 * conhecido, e o código vai cru quando não é — o admission é de outro dono e pode crescer o vocabulário antes da tela.
 */
function RegisterError({ error }: { error: ApiError }) {
  const code = admissionCode(error);
  if (!code) return <ErrorBanner error={error} title={t('sites.admission.register.error.title')} hideLink />;
  const key = `sites.admission.admissionCode.${code}`;
  return (
    <Alert tone="danger" title={t('sites.admission.register.error.title')}>
      {hasKey(key) ? t(key) : t('sites.admission.admissionCode.unknown', { code })}
    </Alert>
  );
}
